import { useState, useRef, useEffect, useMemo } from "react";
import { ChevronDown, X, Search } from "lucide-react";

/* Reusable type-to-filter select.
   Props:
     value       selected option id (string)
     onChange    (id) => void
     options     [{ id, name }]
     placeholder string
     disabled    bool
   Shows the selected option's name in the trigger; typing filters the list.
   Matches the wizard field styling (ring-1 ring-slate-300 rounded-lg). */
export function SearchSelect({ value, onChange, options = [], placeholder = "Search\u2026", disabled = false }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find((o) => o.id === value) || null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.name || "").toLowerCase().startsWith(q));
  }, [query, options]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQuery(""); } };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); setQuery(""); } };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const fieldCls = "w-full text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 bg-white focus:outline-none focus:ring-blue-500";

  const pick = (id) => { onChange(id); setOpen(false); setQuery(""); };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={fieldCls + " flex items-center justify-between text-left " + (disabled ? " bg-slate-50 cursor-not-allowed text-slate-400" : "")}
      >
        <span className={selected ? "text-slate-900 truncate" : "text-slate-400 truncate"}>
          {selected ? selected.name : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0 ml-2">
          {selected && !disabled && (
            <X
              className="w-3.5 h-3.5 text-slate-400 hover:text-slate-700"
              onClick={(e) => { e.stopPropagation(); pick(""); }}
            />
          )}
          <ChevronDown className="w-4 h-4 text-slate-400" />
        </span>
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full bg-white rounded-lg ring-1 ring-slate-200 shadow-lg overflow-hidden">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100">
            <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Type to search\u2026"
              className="w-full text-sm bg-transparent focus:outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => pick(o.id)}
                  className={"w-full text-left px-3 py-2 text-sm hover:bg-slate-50 " + (o.id === value ? "bg-blue-50 text-blue-700 font-medium" : "text-slate-700")}
                >
                  {o.name}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
